/**
 * The child's side: pick something, see what it costs, then ask.
 *
 * The order is the design. The impact appears as soon as there is an amount,
 * before the ask button does anything — so the arithmetic is unavoidable and
 * the decision to ask is made *after* seeing it. Filing the request is still
 * one tap, because a child who has looked at the numbers and still wants the
 * thing has done exactly what this feature is for.
 *
 * Declining costs nothing and is not discouraged anywhere in this component.
 */

import { useState } from 'react';
import SpendImpact from './SpendImpact.jsx';
import { TileIcon } from './Art.jsx';
import { centsFromDollars, formatCents } from '../domain/money.js';

export default function AskFlow({ tiles, balanceCents, goal, refillCents, cadence, onAsk }) {
  const [tileId, setTileId] = useState(null);
  const [dollars, setDollars] = useState('');
  const [note, setNote] = useState('');

  const tile = tiles.find((t) => t.id === tileId) ?? null;
  const amountCents = tile ? tile.defaultAmountCents : centsFromDollars(dollars);
  const hasAmount = amountCents > 0;

  function reset() { setTileId(null); setDollars(''); setNote(''); }

  return (
    <section className="card stack" aria-labelledby="ask-heading">
      <p className="eyebrow" id="ask-heading">Ask for something</p>

      <div className="tiles">
        {tiles.map((t) => (
          <button
            key={t.id}
            className="tile tile--pick"
            aria-pressed={tileId === t.id}
            onClick={() => { setTileId(tileId === t.id ? null : t.id); setDollars(''); }}
          >
            <TileIcon tile={t} />
            <span className="tile__label">{t.label}</span>
            <span className="tile__amount">{formatCents(t.defaultAmountCents)}</span>
          </button>
        ))}
      </div>

      <label>
        Or something else, in dollars
        <input
          type="number" inputMode="decimal" min="0" step="0.01" value={dollars}
          placeholder="0.00"
          onChange={(e) => { setDollars(e.target.value); setTileId(null); }}
        />
      </label>

      {hasAmount && (
        <>
          <SpendImpact
            balanceCents={balanceCents}
            amountCents={amountCents}
            goal={goal}
            refillCents={refillCents}
            cadence={cadence}
          />
          <label>
            What is it? (optional)
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={80} placeholder="Cinema with Sam" />
          </label>
          <button
            className="primary"
            onClick={() => { onAsk({ amountCents, tileId, note: note.trim() || null }); reset(); }}
          >
            Ask
          </button>
        </>
      )}
    </section>
  );
}
